import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Optional,
  Output
} from "@angular/core";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {ResourceWithUtilizationScoring} from "../../../models/Resource-with-utilization-scoring.model";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {
  VisitPlanExternalAssignRequests
} from "../../../models/visit-plan-create-external-resource-holders-payload.model";
import {VisitScheduleAgentImportOptionModel} from "../../../models/visit-schedule-agent-import-option.model";
import {VisitPlanFacadeService} from "../../services/visit-plan-facade.service";
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";
import {ToastService} from "../../../../commons/angular/services/toast.service";
import {finalize} from "rxjs";
import * as moment from "moment";

interface VisitPlanScheduleRouteReassignmentPanelData {
  resources: ResourceWithUtilizationScoring[];
  visitPlan: VisitPlanModel;
  visitScheduleId: string;
  dateRange: { from: number, to: number };
}

require('./visit-plan-schedule-route-reassignment.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-schedule-route-reassignment',
  templateUrl: './visit-plan-schedule-route-reassignment.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanScheduleRouteReassignmentComponent implements OnInit {

  @Output() onClose = new EventEmitter<string>();

  public selectedResourceId: string;
  public externalAssignPayload: VisitPlanExternalAssignRequests;
  public isExternalResourcesMode: boolean = false;
  public queryString: string = '';
  private _options: ResourceWithUtilizationScoring[] = [];
  public optionsToDisplay: ResourceWithUtilizationScoring[] = [];
  public isLoading: boolean;
  public assigning: boolean = false;
  public errorMessage: string = '';


  constructor(@Optional() @Inject(PANEL_DATA) public data: VisitPlanScheduleRouteReassignmentPanelData,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
              @Inject(VisitPlanFacadeService) private visitPlanFacadeService: VisitPlanFacadeService,
              @Inject(ToastService) public toastService: ToastService,
  ) {
  }

  ngOnInit() {
    this._options = this.data.resources;
    this.optionsToDisplay = [...this._options];
  }

  onSelectResource(resourceId) {
    this.selectedResourceId = resourceId;
  }

  onSubmit() {
    if (this.selectedResourceId) {
      this.onClose.emit(this.selectedResourceId);
      return;
    }

    if (!this.validateTimeRange()) {
      this.cdr.detectChanges();
      return;
    }

    this.assigning = true;
    this.visitPlanFacadeService.importExternalAgentsResourceHoldersDesignated(this.data.visitPlan.id, this.data.visitScheduleId, [this.externalAssignPayload])
      .pipe(finalize(() => {
        this.assigning = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (resourceHolders: VisitScheduleResourceHolderExt[]) => {
          this.onClose.emit(resourceHolders[0].id);
        },
        error: () => {
          this.toastService.error('AGENT_CREATE_ERROR');
        }
      });
  }

  onCancel() {
    this.onClose.emit();
  }

  onToggleIsExternalResourcesMode(isExternalResourcesMode) {
    this.isExternalResourcesMode = isExternalResourcesMode;
    this.externalAssignPayload = !!isExternalResourcesMode ?
      {
        startTime: this.data.dateRange?.from,
        endTime: this.data.dateRange?.to,
      } : undefined;

    if (!!isExternalResourcesMode) {
      this.selectedResourceId = undefined;
    }
    this.cdr.detectChanges();
  }

  onSelectExternalResource(option: VisitScheduleAgentImportOptionModel) {
    this.externalAssignPayload = {
      agentId: option.id,
      branchIds: this.data.visitPlan.branchIds,
      startTime: moment(this.data.dateRange?.from).startOf('day').valueOf(),
      endTime: moment(this.data.dateRange?.to).endOf('day').minute(45).seconds(0).millisecond(0).valueOf()
    };
    this.selectedResourceId = undefined;
    this.validateTimeRange();
    this.cdr.detectChanges();
  }

  onFromTimeChange(startTime) {
    this.externalAssignPayload.startTime = startTime;
    this.validateTimeRange();
    this.cdr.detectChanges();
  }

  onToTimeChange(endTime) {
    this.externalAssignPayload.endTime = endTime;
    this.validateTimeRange();
    this.cdr.detectChanges();
  }

  get dayStartTime(): number {
    return this.data.dateRange?.from ? moment(this.data.dateRange.from).startOf('day').valueOf() : 0;
  }

  get dayEndTime(): number {
    return this.data.dateRange?.from ? moment(this.data.dateRange.from).endOf('day').valueOf() : 0;
  }

  onQueryChange(query) {
    this.queryString = query;
    this._filterByQueryString();
  }

  private validateTimeRange(): boolean {
    if (!this.isExternalResourcesMode || !this.externalAssignPayload?.agentId) {
      this.errorMessage = '';
      return true;
    }

    const startTime = this.externalAssignPayload.startTime;
    const endTime = this.externalAssignPayload.endTime;

    if (!startTime || !endTime) {
      this.errorMessage = 'Both start and end times are required';
      return false;
    }

    if (endTime <= startTime) {
      this.errorMessage = 'End time must be after start time';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  private _filterByQueryString() {
    this.isLoading = true;
    this.optionsToDisplay = this._options.filter(option => {
      const {name, skills, tags} = option.resourceHolderReference;

      const matchesName = name.toLowerCase().includes(this.queryString);
      const matchesSkills = skills.some(skill => (skill || '').toLowerCase().includes(this.queryString));
      const matchesTags = tags.some(tag => (tag || '').toLowerCase().includes(this.queryString));

      return matchesName || matchesSkills || matchesTags;
    });
    this.isLoading = false;
    this.cdr.detectChanges();
  }

}
